<script lang="ts">
    import dayjs from 'dayjs';
    import { parseDate } from '@internationalized/date';
    import { onMount, onDestroy } from 'svelte';

    import { OptionList } from '$saga/types/search.types';
    import FilterBadges from '$saga/views/Search/Filters/FilterBadges.svelte';
    import {
        findCurrentSelectedFilterLabel,
        initializeLabelValues,
    } from '$saga/views/Search/utils';
    import MainFilters from '$saga/views/Search/MainFilters/MainFilters.svelte';
    import { filtersContext } from '$saga/views/Search/store';
    import { SearchForm, Sorting } from './types';
    import Sort from '$saga/views/Search/Sort.svelte';

    export let defaultDestinationOptions: OptionList;
    export let defaultSportOptions: OptionList;
    export let defaultDurationMin: number;
    export let defaultDurationMax: number;
    export let defaultBudgetMin: number;
    export let defaultBudgetMax: number;
    export let defaultLevels: OptionList;
    export let defaultTags: OptionList;
    export let defaultTypes: OptionList;
    export let searchForm: SearchForm | undefined = undefined;
    export let isHomePage: boolean = false;

    let sortRef: HTMLInputElement | null = null;

    $filtersContext.formValues = {
        destination: searchForm?.destinations?.map(String) ?? [],
        theme: searchForm?.sports?.map(String) ?? [],
        startDate: searchForm?.startDate?.date
            ? parseDate(dayjs(searchForm.startDate.date).format('YYYY-MM-DD'))
            : undefined,
        endDate: searchForm?.endDate?.date
            ? parseDate(dayjs(searchForm.endDate.date).format('YYYY-MM-DD'))
            : undefined,
        flexibility: searchForm?.flexibility ?? 0,
        nbPax: searchForm?.nbPax?.toString() ?? '0',
        // adults: searchForm?.nbPax?.toString() ?? '0',
        // children: '0',
        // childrenAges: [],
        // secondary
        levels: searchForm?.levels ?? [],
        types: searchForm?.types ?? [],
        durationMin: searchForm?.durationMin ?? defaultDurationMin,
        durationMax: searchForm?.durationMax ?? defaultDurationMax,
        budgetMin: searchForm?.budgetMin ?? defaultBudgetMin,
        budgetMax: searchForm?.budgetMax ?? defaultBudgetMax,
        tags: searchForm?.tags ?? [],
    };

    $filtersContext.formLabels = initializeLabelValues(
        $filtersContext.formValues,
        defaultDestinationOptions,
        defaultSportOptions,
        defaultLevels,
        defaultTypes,
        defaultTags,
        defaultDurationMin,
        defaultDurationMax,
        defaultBudgetMin,
        defaultBudgetMax,
    );
    $filtersContext.defaultDurationMin = defaultDurationMin;
    $filtersContext.defaultDurationMax = defaultDurationMax;
    $filtersContext.defaultBudgetMin = defaultBudgetMin;
    $filtersContext.defaultBudgetMax = defaultBudgetMax;
    $filtersContext.defaultLevels = defaultLevels;
    $filtersContext.defaultTags = defaultTags;
    $filtersContext.defaultTypes = defaultTypes;
    $filtersContext.defaultSportOptions = defaultSportOptions;
    $filtersContext.defaultDestinationOptions = defaultDestinationOptions;
    $filtersContext.isHomePage = isHomePage;
    $filtersContext.isSubmitting = false;

    const handleFormSubmissionAnalytics = () => {
        if (!('dataLayer' in window)) {
            return;
        }
        // TODO send this "filter" analytic as multiple events ?
        // let levelLabel =
        //     $filtersContext.formValues.levels.map((level) =>
        //         findCurrentSelectedFilterLabel(level, defaultOptions.levels),
        //     ) ?? 'without_search';
        // let monthLabel = 'without_search';
        // let tagLabel =
        //     $filtersContext.formValues.tags.map((tag) =>
        //         findCurrentSelectedFilterLabel(tag, defaultOptions.tags),
        //     ) ?? 'without_search';
        // let typeLabel =
        //     $filtersContext.formValues.types.map((type) =>
        //         findCurrentSelectedFilterLabel(type, defaultOptions.types),
        //     ) ?? 'without_search';
        // const secondaryFilters = [
        //     { name: 'levels', value: levelLabel },
        //     { name: 'month', value: monthLabel },
        //     { name: 'themes', value: tagLabel },
        //     { name: 'types', value: typeLabel },
        //     {
        //         name: 'duration',
        //         value: `${$filtersContext.formValues.durationMin} - ${$filtersContext.formValues.durationMax} jours`,
        //     },
        //     {
        //         name: 'budget',
        //         value: `${$filtersContext.formValues.budgetMin} - ${$filtersContext.formValues.budgetMax} €`,
        //     },
        // ];
        // for (const filter of secondaryFilters) {
        //     window.dataLayer.push({
        //         event: 'santianoEcommerce',
        //         eventCommand: 'filter',
        //         filterName: filter.name,
        //         filterValue: filter.value,
        //     });
        // }

        let departureDate =
            $filtersContext.formValues.startDate?.toString() ??
            'without_search';
        let travelers =
            $filtersContext.formValues.nbPax?.toString() ?? 'without_search';
        let destinationLabels =
            $filtersContext.formValues.destination.map((destination) =>
                findCurrentSelectedFilterLabel(
                    destination,
                    defaultDestinationOptions,
                ),
            ) ?? 'without_search';
        let sportLabels =
            $filtersContext.formValues.theme.map((theme) =>
                findCurrentSelectedFilterLabel(theme, defaultSportOptions),
            ) ?? 'without_search';

        if (travelers === '0') {
            travelers = 'without_search';
        }
        window.dataLayer.push({
            event: 'santianoEcommerce',
            eventCommand: 'search',
            destinations: destinationLabels,
            sports: sportLabels,
            departureDate: departureDate,
            travelers: travelers,
        });
    };

    const submitButtonEvent = () => {
        $filtersContext.isSubmitting = true;
        handleFormSubmissionAnalytics();
    };

    const resetButtonEvent = () => {
        $filtersContext.isSubmitting = false;
    };

    const setValueAndSubmitForm = () => {
        sortRef?.form?.submit();
    };

    onMount(() => {
        // listen for the form submit event and add a load spinner on the submitButtonElement
        const form = document.getElementById('travel-search-engine-form');
        if (form) {
            form.addEventListener('submit', submitButtonEvent);
            form.addEventListener('reset', resetButtonEvent);
        }
        sortRef?.addEventListener('change', setValueAndSubmitForm);
    });

    onDestroy(() => {
        // remove event listener on form submit
        const form = document.getElementById('travel-search-engine-form');
        if (form) {
            form.removeEventListener('submit', submitButtonEvent);
            form.removeEventListener('reset', resetButtonEvent);
        }
        sortRef?.removeEventListener('change', setValueAndSubmitForm);
    });
</script>

<MainFilters {sortRef} />

{#if !isHomePage && searchForm}
    <div
        class="mx-auto my-m hidden max-w-layout-max flex-wrap items-center justify-between desktop:flex"
    >
        <FilterBadges {searchForm} />
        <!-- sort on desktop -->
        <div class="ml-auto mr-5xl hidden desktop:block">
            <Sort {sortRef} />
        </div>
    </div>
{/if}

<!-- real input attached to the form, hidden -->
<input
    class="hidden"
    bind:this={sortRef}
    name="sort"
    value={searchForm?.sort ?? Sorting.DEFAULT}
/>
<!-- main form filters, hidden -->
<!--Desti -->
{#each $filtersContext.formValues.destination as value}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="destination[]"
        {value}
    />
{/each}

<!--Sport -->
{#each $filtersContext.formValues.theme as value}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="theme[]"
        {value}
    />
{/each}

<!--    startDate -->
<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="startDate"
    value={$filtersContext.formValues.startDate
        ? dayjs($filtersContext.formValues.startDate.toString()).format(
              'DD/MM/YYYY',
          )
        : ''}
/>
<!--    endDate -->
<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="endDate"
    value={$filtersContext.formValues.endDate
        ? dayjs($filtersContext.formValues.endDate.toString()).format(
              'DD/MM/YYYY',
          )
        : ''}
/>

<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="flexibility"
    value={$filtersContext.formValues.flexibility}
/>

<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="nbPax"
    value={Number($filtersContext.formValues.nbPax)}
/>

<!-- Secondary filters inputs -->
{#each $filtersContext.formValues.levels as level}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="level[]"
        value={level}
    />
{/each}

{#each $filtersContext.formValues.types as type}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="type[]"
        value={type}
    />
{/each}

<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="durationMin"
    value={$filtersContext.formValues.durationMin}
/>

<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="durationMax"
    value={$filtersContext.formValues.durationMax}
/>
<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="budgetMin"
    value={$filtersContext.formValues.budgetMin}
/>
<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="budgetMax"
    value={$filtersContext.formValues.budgetMax}
/>

{#each $filtersContext.formValues.tags as tag}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="tag[]"
        value={tag}
    />
{/each}
